class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    FacetFiltersForm.openVerticalFiltersByDefault();
    FacetFiltersForm.bindVerticalFilterState();
    FacetFiltersForm.initSmoothDropdowns();
    FacetFiltersForm.initCustomSortDropdowns();
  }

  static verticalFilterState = {};

  static bindVerticalFilterState() {
    if (FacetFiltersForm.verticalFilterListenerBinded) return;
    FacetFiltersForm.verticalFilterListenerBinded = true;

    document.addEventListener('click', (event) => {
      const summary = event.target.closest('#FacetFiltersForm .js-filter > summary, #FacetFiltersForm .js-filter .facets__summary');
      if (!summary) return;

      const details = summary.closest('.js-filter');
      if (!details || !details.id) return;

      setTimeout(() => {
        FacetFiltersForm.verticalFilterState[details.id] = details.open;
      }, 350);
    });
  }

  static initSmoothDropdowns(root = document) {
    root.querySelectorAll('.js-filter').forEach((details) => {
      if (details.dataset.smoothReady === 'true') return;

      const summary = details.querySelector('summary');
      const content =
        details.querySelector('.facets__display-vertical') ||
        details.querySelector('.facets__display') ||
        details.querySelector('.mobile-facets__submenu');

      if (!summary || !content) return;

      details.dataset.smoothReady = 'true';
      content.style.overflow = 'hidden';

      if (details.open) {
        content.style.maxHeight = 'none';
      } else {
        content.style.maxHeight = '0px';
      }

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (details.dataset.animating === 'true') return;
        details.dataset.animating = 'true';

        const isOpen = details.hasAttribute('open');

        if (isOpen) {
          content.style.maxHeight = `${content.scrollHeight}px`;

          requestAnimationFrame(() => {
            content.style.maxHeight = '0px';
          });

          setTimeout(() => {
            details.removeAttribute('open');
            details.dataset.animating = 'false';
            FacetFiltersForm.verticalFilterState[details.id] = false;
          }, 320);
        } else {
          details.setAttribute('open', '');
          content.style.maxHeight = '0px';

          requestAnimationFrame(() => {
            content.style.maxHeight = `${content.scrollHeight}px`;
          });

          setTimeout(() => {
            content.style.maxHeight = 'none';
            details.dataset.animating = 'false';
            FacetFiltersForm.verticalFilterState[details.id] = true;
          }, 320);
        }
      });
    });
  }

  static initCustomSortDropdowns(root = document) {
    root.querySelectorAll('.custom-sort-dropdown').forEach((dropdown) => {
      if (dropdown.dataset.customSortReady === 'true') return;

      const button = dropdown.querySelector('.custom-sort-dropdown__button');
      const list = dropdown.querySelector('.custom-sort-dropdown__list');
      const options = dropdown.querySelectorAll('.custom-sort-dropdown__option');
      const select = dropdown.parentElement?.querySelector('select[name="sort_by"]') || document.querySelector('select[name="sort_by"]');

      if (!button || !list || !select) return;

      dropdown.dataset.customSortReady = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        dropdown.classList.toggle('is-open');
      });

      options.forEach((option) => {
        option.addEventListener('click', (event) => {
          event.preventDefault();

          const value = option.dataset.value;
          const text = option.textContent.trim();

          select.value = value;
          button.querySelector('.custom-sort-dropdown__selected').textContent = text;

          options.forEach((item) => item.classList.remove('is-selected'));
          option.classList.add('is-selected');

          dropdown.classList.remove('is-open');

          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      document.addEventListener('click', (event) => {
        if (!dropdown.contains(event.target)) dropdown.classList.remove('is-open');
      });
    });
  }

  static openVerticalFiltersByDefault(root = document) {
    root.querySelectorAll('#FacetFiltersForm .js-filter').forEach((details) => {
      if (!details.id) return;

      if (Object.prototype.hasOwnProperty.call(FacetFiltersForm.verticalFilterState, details.id)) {
        if (FacetFiltersForm.verticalFilterState[details.id]) {
          details.setAttribute('open', '');
        } else {
          details.removeAttribute('open');
        }
      } else {
        details.setAttribute('open', '');
      }
    });

    FacetFiltersForm.initSmoothDropdowns(root);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer && productGridContainer.querySelector('.collection')) {
      productGridContainer.querySelector('.collection').classList.add('loading');
    }

    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.openVerticalFiltersByDefault();
        FacetFiltersForm.initSmoothDropdowns();
        FacetFiltersForm.initCustomSortDropdowns();

        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.openVerticalFiltersByDefault();
    FacetFiltersForm.initSmoothDropdowns();
    FacetFiltersForm.initCustomSortDropdowns();

    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (sourceCount && container) {
      container.innerHTML = sourceCount.innerHTML;
      container.classList.remove('loading');
    }

    if (sourceCount && containerDesktop) {
      containerDesktop.innerHTML = sourceCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
        currentElement.removeAttribute('data-smooth-ready');
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];

          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.openVerticalFiltersByDefault();
    FacetFiltersForm.initSmoothDropdowns();
    FacetFiltersForm.initCustomSortDropdowns();

    if (countsToRender && event) {
      const closestFilter = event.target.closest('.js-filter');
      const closestJSFilterID = closestFilter ? closestFilter.id : null;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, closestFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
        FacetFiltersForm.openVerticalFiltersByDefault();
        FacetFiltersForm.initSmoothDropdowns();

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const currentElement = document.querySelector(selector);
      if (currentElement) currentElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const currentElement = document.querySelector(selector);
      if (currentElement) currentElement.innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm && mobileForm.closest('menu-drawer') && typeof mobileForm.closest('menu-drawer').bindEvents === 'function') {
      mobileForm.closest('menu-drawer').bindEvents();
    }

    FacetFiltersForm.initCustomSortDropdowns();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }

    target.removeAttribute('data-smooth-ready');
    FacetFiltersForm.initSmoothDropdowns(target.parentElement || document);
  }

  static renderMobileCounts(source, target) {
    if (!target) return;

    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }

    target.removeAttribute('data-smooth-ready');
    FacetFiltersForm.initSmoothDropdowns(target.parentElement || document);
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });

      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minTextInput = this.querySelector('.price-min-input');
    this.maxTextInput = this.querySelector('.price-max-input');
    this.minRangeInput = this.querySelector('.price-min-range');
    this.maxRangeInput = this.querySelector('.price-max-range');

    if (!this.minTextInput || !this.maxTextInput || !this.minRangeInput || !this.maxRangeInput) return;

    this.minGap = 0;

    this.minRangeInput.addEventListener('input', this.onRangeInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.onRangeInput.bind(this));

    this.minRangeInput.addEventListener('change', this.dispatchFilterChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.dispatchFilterChange.bind(this));

    this.minTextInput.addEventListener('change', this.onTextInput.bind(this));
    this.maxTextInput.addEventListener('change', this.onTextInput.bind(this));

    this.querySelectorAll('input[type="text"]').forEach((input) => {
      input.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    this.updateValues();
  }

  getNumber(value) {
    return Number(String(value).replace(/,/g, '')) || 0;
  }

  onRangeInput() {
    let minValue = this.getNumber(this.minRangeInput.value);
    let maxValue = this.getNumber(this.maxRangeInput.value);

    if (minValue > maxValue - this.minGap) {
      minValue = maxValue - this.minGap;
      this.minRangeInput.value = minValue;
    }

    if (maxValue < minValue + this.minGap) {
      maxValue = minValue + this.minGap;
      this.maxRangeInput.value = maxValue;
    }

    this.minTextInput.value = minValue;
    this.maxTextInput.value = maxValue;

    this.updateValues();
  }

  onTextInput() {
    const maxLimit = this.getNumber(this.maxTextInput.dataset.max);

    let minValue = this.getNumber(this.minTextInput.value);
    let maxValue = this.getNumber(this.maxTextInput.value);

    if (minValue < 0) minValue = 0;
    if (maxValue > maxLimit) maxValue = maxLimit;
    if (minValue > maxValue) minValue = maxValue;

    this.minTextInput.value = minValue;
    this.maxTextInput.value = maxValue;
    this.minRangeInput.value = minValue;
    this.maxRangeInput.value = maxValue;

    this.updateValues();
    this.dispatchFilterChange();
  }

  updateValues() {
    const max = this.getNumber(this.maxRangeInput.max);
    const minValue = this.getNumber(this.minRangeInput.value);
    const maxValue = this.getNumber(this.maxRangeInput.value);

    const minPercent = max > 0 ? (minValue / max) * 100 : 0;
    const maxPercent = max > 0 ? (maxValue / max) * 100 : 100;

    const track = this.querySelector('.price-slider-track');
    if (track) {
      track.style.left = `${minPercent}%`;
      track.style.right = `${100 - maxPercent}%`;
    }
  }

  dispatchFilterChange() {
    this.minTextInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink = this.querySelector('a');
    if (!facetLink) return;

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);